import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Heart, Users, FileText, ArrowRight, Target,
  Globe, BookOpen, Star, ChevronRight, HandHeart
} from 'lucide-react';
import { noticiaService, projetoService, membroService } from '../services/api';
import './Home.css';

export default function Home() {
  const [noticias, setNoticias]   = useState<any[]>([]);
  const [projetos, setProjetos]   = useState<any[]>([]);
  const [membros,  setMembros]    = useState<any[]>([]);

  useEffect(() => {
    Promise.all([
      noticiaService.listar({ limite: 3, destaque: 'true' }).catch(() => ({ data: { noticias: [] } })),
      projetoService.listar().catch(() => ({ data: [] })),
      membroService.listar().catch(() => ({ data: [] })),
    ]).then(([n, p, m]) => {
      setNoticias(n.data.noticias || []);
      setProjetos((Array.isArray(p.data) ? p.data : []).slice(0, 3));
      setMembros((Array.isArray(m.data) ? m.data : []).slice(0, 4));
    });
  }, []);

  return (
    <main className="home">
      {/* ── HERO ── */}
      <section className="hero">
        <div className="hero-blobs">
          <div className="blob" style={{ width:500,height:500,background:'var(--rosa)',top:'-150px',right:'-100px' }}/>
          <div className="blob" style={{ width:350,height:350,background:'var(--amarelo)',bottom:'-80px',left:'-60px' }}/>
          <div className="blob" style={{ width:280,height:280,background:'var(--turquesa)',top:'40%',left:'30%' }}/>
        </div>
        <div className="container hero-inner">
          <div className="hero-content anim-fadeup">
            <div className="hero-badge">
              <HandHeart size={14} /> Instituto Mãos Estendidas · Marabá – PA
            </div>
            <h1>
              Transformando vidas<br />
              <span className="hero-accent">com amor e ação</span>
            </h1>
            <p>
              Somos uma organização não governamental comprometida com o desenvolvimento
              social, educação, saúde e cultura em Marabá e região, atuando sem
              discriminação de classe, raça ou crença.
            </p>
            <div className="hero-cta">
              <Link to="/projetos" className="btn btn-primary btn-lg">
                Nossos Projetos <ArrowRight size={18} />
              </Link>
              <Link to="/quem-somos" className="btn btn-outline btn-lg">
                Saiba mais
              </Link>
            </div>
          </div>
          <div className="hero-stats">
            {[
              { icon: <Heart size={22}/>, num: '10+', label: 'Anos de impacto' },
              { icon: <Users size={22}/>, num: '500+', label: 'Vidas impactadas' },
              { icon: <Target size={22}/>, num: '20+', label: 'Projetos ativos' },
              { icon: <Globe size={22}/>, num: '5+', label: 'Parceiros' },
            ].map((s, i) => (
              <div className="stat-card" key={i} style={{ animationDelay: `${i * 0.1}s` }}>
                <div className="stat-icon">{s.icon}</div>
                <span className="stat-num">{s.num}</span>
                <span className="stat-label">{s.label}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="hero-wave">
          <svg viewBox="0 0 1440 80" preserveAspectRatio="none">
            <path d="M0,40 C360,80 1080,0 1440,40 L1440,80 L0,80 Z" fill="var(--creme)" />
          </svg>
        </div>
      </section>

      {/* ── SOBRE ── */}
      <section className="section-pad sobre-section">
        <div className="container">
          <div className="sobre-grid">
            <div className="sobre-text">
              <div className="section-title" style={{ textAlign: 'left', marginBottom: 32 }}>
                <span className="label">Nossa Missão</span>
                <h2>Quem somos e o que fazemos</h2>
                <p>
                  O Instituto Mãos Estendidas (IME) é uma ONG de direito privado, sem fins econômicos,
                  sediada em Marabá – PA, atuando em todo o Estado do Pará.
                </p>
              </div>
              <div className="sobre-pillars">
                {[
                  { icon: <BookOpen size={18}/>, title: 'Educação', desc: 'Cursos, capacitações e assessoria para transformar comunidades.' },
                  { icon: <Heart size={18}/>, title: 'Saúde', desc: 'Ações de prevenção e assistência médica para todos.' },
                  { icon: <Users size={18}/>, title: 'Social', desc: 'Projetos de geração de renda e desenvolvimento comunitário.' },
                  { icon: <Globe size={18}/>, title: 'Cultura', desc: 'Preservação e promoção das expressões artísticas locais.' },
                ].map((p, i) => (
                  <div className="pillar" key={i}>
                    <div className="pillar-icon">{p.icon}</div>
                    <div>
                      <strong>{p.title}</strong>
                      <p>{p.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <Link to="/quem-somos" className="btn btn-primary mt-24">
                Conheça nossa história <ChevronRight size={16} />
              </Link>
            </div>
            <div className="sobre-visual">
              <div className="sobre-card-main">
                <div className="sobre-icon-big"><HandHeart size={64} /></div>
                <h3>Transparência como compromisso</h3>
                <p>Acesse nossos relatórios, atas e documentos legais neste portal.</p>
                <Link to="/documentos" className="btn btn-outline mt-16">Ver documentos</Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── PROJETOS ── */}
      {projetos.length > 0 && (
        <section className="section-pad projetos-section">
          <div className="container">
            <div className="section-title">
              <span className="label">O que fazemos</span>
              <h2>Projetos em andamento</h2>
              <p>Conheça as iniciativas que estão mudando vidas em Marabá e região.</p>
            </div>
            <div className="grid-3">
              {projetos.map((p: any, i: number) => (
                <div className="card projeto-card" key={p._id} style={{ animationDelay: `${i * 0.1}s` }}>
                  {p.imagemUrl && (
                    <div className="projeto-img">
                      <img src={p.imagemUrl} alt={p.nome} loading="lazy" />
                    </div>
                  )}
                  <div className="projeto-body">
                    {p.categoria && <span className="badge badge-laranja">{p.categoria}</span>}
                    <h3>{p.nome}</h3>
                    <p>{p.descricao?.slice(0, 120)}{p.descricao?.length > 120 ? '...' : ''}</p>
                    <Link to={`/projetos/${p._id}`} className="projeto-link">
                      Saiba mais <ArrowRight size={15} />
                    </Link>
                  </div>
                </div>
              ))}
            </div>
            <div className="text-center mt-32">
              <Link to="/projetos" className="btn btn-outline">Ver todos os projetos</Link>
            </div>
          </div>
        </section>
      )}

      {/* ── TRANSPARÊNCIA BANNER ── */}
      <section className="transparencia-banner">
        <div className="container">
          <div className="trans-content">
            <div>
              <FileText size={36} />
              <h2>Portal de Transparência</h2>
              <p>
                Acesse estatutos, atas, relatórios financeiros e todos os documentos
                oficiais do Instituto Mãos Estendidas. Compromisso com a transparência.
              </p>
            </div>
            <div className="trans-links">
              <Link to="/documentos?categoria=estatuto" className="trans-link">
                <Star size={15} /> Estatuto Social
              </Link>
              <Link to="/documentos?categoria=ata" className="trans-link">
                <Star size={15} /> Atas de Reunião
              </Link>
              <Link to="/documentos?categoria=relatorio" className="trans-link">
                <Star size={15} /> Relatórios
              </Link>
              <Link to="/documentos" className="btn btn-ghost btn-lg mt-16">
                Acessar todos os documentos <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── NOTÍCIAS ── */}
      {noticias.length > 0 && (
        <section className="section-pad noticias-section">
          <div className="container">
            <div className="section-title">
              <span className="label">Fique por dentro</span>
              <h2>Últimas Notícias</h2>
              <p>Acompanhe as novidades, projetos e conquistas do Instituto.</p>
            </div>
            <div className="grid-3">
              {noticias.map((n: any, i: number) => (
                <Link to={`/noticias/${n.slug}`} className="card noticia-card" key={n._id} style={{ animationDelay: `${i * 0.1}s` }}>
                  {n.imagemUrl && (
                    <div className="noticia-img">
                      <img src={n.imagemUrl} alt={n.titulo} loading="lazy" />
                      {n.categoria && <span className="noticia-cat">{n.categoria}</span>}
                    </div>
                  )}
                  <div className="noticia-body">
                    <h4>{n.titulo}</h4>
                    {n.subtitulo && <p>{n.subtitulo}</p>}
                    <span className="noticia-data">
                      {new Date(n.criadoEm).toLocaleDateString('pt-BR', { day:'2-digit', month:'long', year:'numeric' })}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
            <div className="text-center mt-32">
              <Link to="/noticias" className="btn btn-outline">Ver todas as notícias</Link>
            </div>
          </div>
        </section>
      )}

      {/* ── EQUIPE ── */}
      {membros.length > 0 && (
        <section className="section-pad equipe-section">
          <div className="container">
            <div className="section-title">
              <span className="label">Nossa equipe</span>
              <h2>Diretoria</h2>
              <p>Conheça as pessoas que lideram o Instituto Mãos Estendidas.</p>
            </div>
            <div className="grid-4">
              {membros.map((m: any) => (
                <div className="card membro-card" key={m._id}>
                  <div className="membro-avatar">
                    {m.fotoUrl
                      ? <img src={m.fotoUrl} alt={m.nome} />
                      : <span>{m.nome?.charAt(0)}</span>
                    }
                  </div>
                  <div className="membro-info">
                    <h4>{m.nome}</h4>
                    <span>{m.cargo}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="text-center mt-32">
              <Link to="/quem-somos" className="btn btn-outline">Ver toda a equipe</Link>
            </div>
          </div>
        </section>
      )}

      {/* ── CTA ── */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-box">
            <div className="cta-blobs">
              <div className="blob" style={{ width:300,height:300,background:'var(--amarelo)',top:'-60px',right:'-60px' }}/>
              <div className="blob" style={{ width:200,height:200,background:'var(--turquesa)',bottom:'-40px',left:'-40px' }}/>
            </div>
            <div className="cta-content">
              <h2>Quer fazer parte dessa transformação?</h2>
              <p>Entre em contato conosco e descubra como você pode contribuir com o Instituto Mãos Estendidas.</p>
              <div className="cta-buttons">
                <Link to="/contato" className="btn btn-primary btn-lg">Entrar em contato</Link>
                <Link to="/galeria" className="btn btn-ghost btn-lg">Ver nossa galeria</Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}